import { useCallback, useEffect, useRef, useState } from 'react'

type VisibleAutoRefreshOptions = {
  enabled?: boolean
  intervalMs?: number
}

/** Runs one refresh at a time, pauses in background tabs, and refreshes immediately on return. */
export function useVisibleAutoRefresh<T>(callback: () => Promise<T>, options: VisibleAutoRefreshOptions = {}) {
  const { enabled = true, intervalMs = 20_000 } = options
  const callbackRef = useRef(callback)
  const inFlight = useRef<Promise<T | undefined> | undefined>(undefined)
  const queued = useRef(false)
  const mounted = useRef(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date>()

  useEffect(() => { callbackRef.current = callback }, [callback])
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const refresh = useCallback((silent = false): Promise<T | undefined> => {
    if (inFlight.current) {
      if (!silent) queued.current = true
      return inFlight.current
    }
    if (mounted.current) setRefreshing(true)
    const request = Promise.resolve()
      .then(() => callbackRef.current())
      .then(result => {
        if (mounted.current) setLastSyncedAt(new Date())
        return result
      })
      .catch(() => undefined)
      .finally(() => {
        inFlight.current = undefined
        if (mounted.current) setRefreshing(false)
        if (queued.current && mounted.current) {
          queued.current = false
          window.setTimeout(() => { void refresh(false) }, 0)
        }
      })
    inFlight.current = request
    return request
  }, [])

  useEffect(() => {
    if (!enabled) return
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh(true)
    }, intervalMs)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh(true)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled, intervalMs, refresh])

  return { refresh, refreshing, lastSyncedAt }
}
